import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import pluralize from 'pluralize';

import Alert from '../../../../../../../../components/global/Alert';
import useCircles from '../../../../../../views/Account/ContactCircles/hooks/useCircles';
import useOpenModal from '../../../../../../../../hooks/useOpenModal';
import Button from '../../../../../../../../components/global/Button';
import { CONTACT_CIRCLE_SAVE_MODAL } from '../../../../../../../../components/global/ContactCircleSaveModal';
import CirclesIcon from '../../../../../../../../components/icons/CirclesIcon';
import ContactCircleTile from '../ContactCircleTile';
import useIsNativeWebView from '../../../../../../../../services/native-bridge/hooks/useIsWebView';
import { postNativeAppCirclesCreateDialog } from '../../../../../../../../services/native-bridge/native-apps-interface';

const propTypes = {
  sendInvites: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
  submitting: PropTypes.bool,
};

const CirclesStep = ({ sendInvites, onSuccess, submitting }) => {
  const [errors, setErrors] = useState([]);
  const [circleInvitees, setCircleInvitees] = useState([]);
  const isNativeView = useIsNativeWebView();
  const { circles, refetchCircles } = useCircles();
  const openModal = useOpenModal();

  useEffect(() => {
    window.shutterbugShareCirclesStepApps = { refetchCircles };
  }, [refetchCircles]);

  const onToggleCircle = circle => {
    if (circleInvitees.includes(circle.id)) {
      setCircleInvitees(
        circleInvitees.filter(circleId => circleId !== circle.id),
      );
    } else {
      setCircleInvitees([...circleInvitees, circle.id]);
    }
  };

  const getCircleContactsTotal = () => {
    if (circleInvitees.length === 0) {
      return 0;
    }

    return circles
      .filter(({ id }) => circleInvitees.includes(id))
      .reduce((sum, circle) => sum + circle.contacts?.total ?? 0, 0);
  };

  const onSendInvites = async () => {
    const sendErrors = await sendInvites([], '', false, circleInvitees);

    if (sendErrors?.length === 0) {
      onSuccess();
    } else {
      setErrors(sendErrors);
    }
  };

  return (
    <form className="mt-2">
      <h4 className="mb-1 w-100 d-flex justify-content-between">
        Invite a circle
        {(circles?.length ?? 0) > 0 && (
          <Button
            btnStyle="link"
            className="d-flex align-items-center text-bold"
            onClick={() => {
              if (isNativeView) {
                postNativeAppCirclesCreateDialog();
              } else {
                openModal(CONTACT_CIRCLE_SAVE_MODAL);
              }
            }}
          >
            <CirclesIcon size={16} className="mr-1" color="#0d84fe" />
            Create circle
          </Button>
        )}
      </h4>
      <div className="share-contact-circles">
        {(circles ?? []).map(circle => (
          <ContactCircleTile
            key={circle.id}
            circle={circle}
            onToggleCircle={() => onToggleCircle(circle)}
            isSelected={circleInvitees.includes(circle.id)}
          />
        ))}
        {(circles?.length ?? 0) === 0 && (
          <Button
            btnStyle="info"
            btnBlock
            className="d-flex align-items-center text-primary text-bold"
            onClick={() => {
              if (isNativeView) {
                postNativeAppCirclesCreateDialog();
              } else {
                openModal(CONTACT_CIRCLE_SAVE_MODAL);
              }
            }}
          >
            <CirclesIcon size={16} className="mr-1" color="#0d84fe" /> Create a
            circle
          </Button>
        )}
      </div>

      {errors.length > 0 && (
        <Alert
          type="danger"
          className="mt-2"
          dismissible
          onDismiss={() => setErrors([])}
        >
          <p>We found some errors with your data:</p>
          <ul>
            {errors.map(error => (
              <li key={error}>{error}</li>
            ))}
          </ul>
        </Alert>
      )}
      {circleInvitees.length > 0 && (
        <Button
          btnBlock
          type="submit"
          disabled={submitting}
          onClick={onSendInvites}
          className="mt-4 text-sm"
        >
          Send link to {getCircleContactsTotal()}{' '}
          {pluralize('person', getCircleContactsTotal())}
        </Button>
      )}
    </form>
  );
};

CirclesStep.propTypes = propTypes;

export default CirclesStep;
